/* Blair TLV product-video fix — shipped as its own asset to bypass global.js's
   sticky ?v= asset cache (Admin-API edits to global.js don't reliably bump ?v=,
   so corrected logic never reached visitors). Self-contained + idempotent.

   Fixes on the product media gallery:
   - Dawn wraps each video in <deferred-media>, un-hidden only at min-width:750px,
     so on mobile the autoplay <video class="product__media-video"> is display:none.
   - The <modal-opener> still paints the poster image, and the raw <video> renders
     at its natural (tall) size -> poster + oversized video "stacked".
   Why a naive height:100% fails: the video's % height resolves against
   <deferred-media>, whose own height is `auto` (it comes from top/bottom:0, not an
   explicit height), so per spec the % falls back to the video's natural height
   (~2x). Fix: measure the media container's pixel height and set it explicitly on
   the wrapper + video, make the container the positioning context with overflow
   hidden, fit the video (object-fit:cover), and hide the duplicate poster.
   Plus: play muted when >=25% in view (only once the page has painted, see below), pause
   when out (battery). Re-applied on load / resize / section load so the pixel height
   tracks the layout. */
(function () {
  function fit(v) {
    var c = v.closest('.product-media-container');
    if (!c) return;
    var dm = v.closest('deferred-media');
    c.style.setProperty('position', 'relative', 'important');
    c.style.setProperty('overflow', 'hidden', 'important');
    // Blair 2026-10-18 (founder: "just crop the video square but keep the product in the center, similar
    // to the preview"): the gallery box is a 1:1 square and the videos are 3:4 (or 9:16). The video used
    // to be sized to its OWN height (width / ratio) and pinned to the top, so the square showed only the
    // top of the frame and cut the pendant off the bottom. It now gets the square's height and a centred
    // cover crop: exactly what the full-screen viewer shows (component-product-modal.css).
    var pm = c.querySelector('.product__modal-opener .product__media');
    if (pm) pm.style.setProperty('visibility', 'hidden', 'important');   // the blurred ::before is the backdrop
    // Compute the target pixel height from the container WIDTH and Dawn's --ratio
    // (set on the container as media.aspect_ratio). This is reliable immediately —
    // unlike clientHeight, it doesn't wait for the lazy-loaded poster image, which
    // on an off-screen slider slide is 0 (then % height fell back to the video's
    // natural ~2x size). Prefer a real clientHeight when we have one.
    // The box is square (product-media-gallery.liquid), so the video gets the box's own height.
    var W = c.clientWidth;
    var H = c.clientHeight || W;
    if (dm) {
      dm.style.setProperty('display', 'block', 'important');
      dm.style.setProperty('position', 'absolute', 'important');
      dm.style.setProperty('top', '0', 'important');
      dm.style.setProperty('left', '0', 'important');
      dm.style.setProperty('width', '100%', 'important');
      if (H > 0) dm.style.setProperty('height', H + 'px', 'important');
      dm.style.setProperty('z-index', '1', 'important');
    }
    v.style.setProperty('display', 'block', 'important');
    v.style.setProperty('position', 'absolute', 'important');
    // 1px overscan on every side: a fractional box edge can never show the video's black backing.
    v.style.setProperty('top', '-1px', 'important');
    v.style.setProperty('left', '-1px', 'important');
    v.style.setProperty('width', 'calc(100% + 2px)', 'important');
    v.style.setProperty('height', (H > 0 ? (H + 2) + 'px' : 'calc(100% + 2px)'), 'important');
    v.style.setProperty('background', 'transparent', 'important');
    // 810px-wide sources are padded by hardware decoders; a 2.5% zoom keeps any dark padding column
    // at the frame's edge outside the box (see sections/slideshow.liquid).
    v.style.setProperty('transform', 'scale(1.025)', 'important');
    v.style.setProperty('object-fit', 'cover', 'important');
    v.style.setProperty('object-position', '50% 50%', 'important');
  }
  /* Blair 2026-10-20 (founder approved): the gallery video no longer downloads as the page opens.
     It used to carry `autoplay`, so the browser fetched the whole HD file (~3.8MB on lotem-ring) in
     parallel with the page and the LCP poster, which delayed the main content on phones. Now the
     markup is `preload="none"` with no `autoplay` (product-thumbnail.liquid): the poster — the same
     image, the same 2.5%-zoomed centred square as before — paints at once as a still, and the video
     only starts once the page has painted (window load, then an idle slot) and its slide is on
     screen. The <source src> stays in the HTML on purpose: that is what Google's video indexing reads
     (with the VideoObject from blair-video-schema.liquid), and preload="none" keeps browsers from
     fetching it.
     Data saver / reduced motion: the still stays, a white play mark sits on it, and a tap plays it
     inline (the next tap opens the viewer, as today). */
  var HOLD = false;
  try {
    HOLD = !!(navigator.connection && navigator.connection.saveData) ||
      !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (err) {}
  var gateOpen = false;
  function start(v) {
    v.dataset.btStarted = '1';
    v.muted = true;
    if (v.preload !== 'auto') v.preload = 'auto';
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  function wake() {
    document.querySelectorAll('.product__media-list video.product__media-video').forEach(function (v) {
      if (!v._btVis || !v.paused) return;
      if (HOLD && !v.dataset.btStarted) return;
      start(v);
    });
  }
  function openGate() {
    if (gateOpen) return;
    gateOpen = true;
    wake();
  }
  (function armGate() {
    var fired = false;
    function idle() {
      if (fired) return; fired = true;
      if ('requestIdleCallback' in window) requestIdleCallback(openGate, { timeout: 2000 });
      else setTimeout(openGate, 300);
    }
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
    // Never hold the video hostage to one slow third-party request: 8s after the HTML is parsed the
    // poster (the LCP, fetched at high priority) is long done.
    function cap() { setTimeout(idle, 8000); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cap, { once: true });
    else cap();
  })();
  var io = null;
  function observer() {
    if (io || !('IntersectionObserver' in window)) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        v._btVis = e.isIntersecting;
        if (e.isIntersecting) {
          if (!gateOpen) return;
          if (HOLD && !v.dataset.btStarted) return;
          start(v);
        } else { try { v.pause(); } catch (err) {} }
      });
    }, { threshold: 0.25 });
    return io;
  }
  // Data saver / reduced motion: the first tap on a held still plays it (window capture runs before
  // product-modal.js's document-level delegate, which would otherwise open the viewer).
  window.addEventListener('click', function (e) {
    if (!HOLD) return;
    var c = e.target && e.target.closest && e.target.closest('.product__media-list .product-media-container.media-type-video');
    var v = c && c.querySelector('video.product__media-video');
    if (!v || v.dataset.btStarted) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    start(v);
  }, true);
  function setup() {
    var vids = document.querySelectorAll('.product__media-list video.product__media-video');
    if (!vids.length) return;
    var ob = observer();
    vids.forEach(function (v) {
      fit(v);
      if (v.dataset.btVfix) return;
      v.dataset.btVfix = '1';
      v.muted = true;
      if (HOLD) v.dataset.btHold = '1';
      // re-fit once the video knows its real dimensions / once images settle
      v.addEventListener('loadedmetadata', function () { fit(v); });
      v.addEventListener('loadeddata', function () { fit(v); });
      if (ob) ob.observe(v);
      else { v._btVis = true; if (gateOpen && !HOLD) start(v); }
      // re-fit when its slider slide scrolls into view (poster may load late)
      var list = v.closest('.product__media-list');
      if (list && !list.dataset.btVfixScroll) {
        list.dataset.btVfixScroll = '1';
        var st;
        list.addEventListener('scroll', function () { clearTimeout(st); st = setTimeout(run, 150); }, { passive: true });
      }
    });
  }
  function run() { setup(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  window.addEventListener('load', run);
  document.addEventListener('shopify:section:load', run);
  // A variant change can move or add gallery slides (product-info.js updateMedia); moving a <video>
  // pauses it, so pick the visible one back up.
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () { setTimeout(function () { run(); if (gateOpen) wake(); }, 0); });
    }
  } catch (err) {}
  // Quick view (Dawn quick-add modal) injects a product's gallery after load: set it up when it opens.
  document.querySelectorAll('quick-add-modal').forEach(function (m) {
    new MutationObserver(function () { if (m.hasAttribute('open')) setTimeout(run, 0); })
      .observe(m, { attributes: true, attributeFilter: ['open'] });
  });
  var rt;
  window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(run, 200); });
  // a couple of delayed passes to catch late layout (poster image load, fonts)
  setTimeout(run, 600);
  setTimeout(run, 1800);
})();
